import AppBar from "@mui/material/AppBar";
import Avatar from "@mui/material/Avatar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import {
  startGetUserLanguage,
  startUpdateUserLanguage,
} from "../../actions/adsCampaigns";
import ButtonCreateAccountFromAnonymous from "../Buttons/ButtonCreateAccountFromAnonymous";
import { BUY_CREDITS, HELP, SUPPORT } from "../../i18n/keysTranslations";
import { startLogout } from "../../actions/auth";
import { openTidioChat } from "../../hooks/useTidio";
import { getNameBusinessTheme } from "../../actions/getters";
import { Link, useLocation } from "react-router-dom";
import { IconButton, useMediaQuery } from "@mui/material";
import { useState, useEffect } from "react";
import {
  PADDING_DESKTOP,
  PADDING_MOBILE,
  OWNER_ROLE,
  PLAN_TYPE_CREDITS,
  FONT_WEIGHT_BUTTON,
  FONT_SIZE_BUTTON,
  IS_WHITE_LABEL_BRAND,
} from "../../utils/constants";
import { useRebrandBanner } from "../../hooks/useRebrandBanner";
import useAgencyIsAppSumo from "../../hooks/useAgencyIsAppSumo";
import { useDefaultAppRoute } from "../../hooks/useIsAdvertisersDemo";
import useSupportSessionsQuota from "../../hooks/useSupportSessionsQuota";
import { setOpenSlideMenu, setModalOpenState } from "../../actions/ui";
import MenuIcon from "@mui/icons-material/Menu";
import { HelpCircle, Headset } from "lucide-react";
import SlideMenu from "../Menus/SlideMenu";
import UsageCounter from "../Billing/UsageCounter";
import { SelectAdvertiserID } from "../Form/SelectAdvertiserID";
import { INPUT_COLOR_BORDER } from "../../utils/colors";
import i18next from "i18next";
import LanguageSwitcher from "../Common/LanguageSwitcher";
import {
  getBrowserAppLanguage,
  persistAppLanguage,
  resolveAppLanguage,
} from "../../utils/i18nLanguage";
import ModalAdHocSupport from "../Modal/ModalAdHocSupport";
import GlobalAlertsInbox from "../Alerts/GlobalAlertsInbox";
import OwnerNavActions, { ownerChrome } from "./OwnerNavActions";

import { LaunchError } from "../../utils/errors";

const ROUTES_WITHOUT_SLIDE_MENU = ["/ads-campaign", "/ads-campaign-statistics"];

const AD_HOC_SUPPORT_MIN_PLAN_PRICE = 499;
const NAV_ICON_SIZE = 20;
const NAV_ICON_STROKE_WIDTH = 2;
const NAV_ICON_TOP_OFFSET = 0.4;

const NAV_ICON_BUTTON_SX = {
  p: 0,
  m: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "inherit",
  "&:hover": {
    backgroundColor: "transparent",
  },
};

const NAV_TEXT_ACTION_SX = {
  fontSize: FONT_SIZE_BUTTON,
  fontWeight: FONT_WEIGHT_BUTTON,
  lineHeight: "normal",
  mb: -0.5,
  color: "text.primary",
};

const NavBar = () => {
  const dispatch = useDispatchApp();
  const { theme, slideMenuIsOpen } = useSelectorApp((state) => state.ui);
  const { user } = useSelectorApp((state) => state.auth);
  const { t } = useTranslationApp();
  const location = useLocation();
  const NAME_BUSINESS = dispatch(getNameBusinessTheme());
  const [userLanguage, setUserLanguage] = useState(null);
  const [isModalAdHocSupportOpen, setIsModalAdHocSupportOpen] = useState(false);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const isOwner = user?.role === OWNER_ROLE;
  const defaultAppRoute = useDefaultAppRoute();
  const homeRoute = isOwner ? "/dashboard" : defaultAppRoute;
  const showMobileNavToggle = !isOwner && isMobile;
  const hideAppBarLogo =
    !isMobile &&
    !isOwner &&
    !ROUTES_WITHOUT_SLIDE_MENU.includes(location.pathname);
  const { typeOfPlans, subscriptionPlan, subscriptionPlanInfo } =
    useSelectorApp((state) => state.payments);
  const { isAppSumo, isAppSumoResolved } = useAgencyIsAppSumo();
  const { canScheduleSupportSession, isSupportSessionsResolved } =
    useSupportSessionsQuota();
  const isAnonymous = user?.isAnonymous;
  const showBuyCreditsButton =
    !!subscriptionPlan &&
    typeOfPlans === PLAN_TYPE_CREDITS &&
    !user.isAnonymous;
  const showAdHocSupportButton =
    !IS_WHITE_LABEL_BRAND &&
    !isAnonymous &&
    !isOwner &&
    !isAppSumo &&
    isAppSumoResolved &&
    isSupportSessionsResolved &&
    canScheduleSupportSession &&
    !!subscriptionPlan &&
    Number(subscriptionPlanInfo?.price) >= AD_HOC_SUPPORT_MIN_PLAN_PRICE;

  const { navbarTop } = useRebrandBanner();

  // Get user language when component loads
  useEffect(() => {
    const getUserLanguage = async () => {
      if (user?.uid) {
        try {
          const language = await dispatch(startGetUserLanguage());
          const resolvedLanguage = resolveAppLanguage({
            userLanguage: language,
            navigatorLanguage: getBrowserAppLanguage(),
          });
          setUserLanguage(resolvedLanguage);
          i18next.changeLanguage(resolvedLanguage);
          persistAppLanguage(resolvedLanguage);
        } catch (error) {
          dispatch(
            LaunchError(error, false, [], {
              functionName: "getUserLanguage",
              capture: "exception",
            })
          );
          console.error("Error al obtener idioma del usuario:", error);
          const fallbackLanguage = getBrowserAppLanguage();
          setUserLanguage(fallbackLanguage);
          i18next.changeLanguage(fallbackLanguage);
          persistAppLanguage(fallbackLanguage);
        }
      }
    };

    getUserLanguage();
  }, [dispatch, user?.uid]);

  const changeLanguage = async (language) => {
    try {
      const normalizedLanguage = persistAppLanguage(language) || language;
      i18next.changeLanguage(normalizedLanguage);
      setUserLanguage(normalizedLanguage);
      await dispatch(startUpdateUserLanguage(normalizedLanguage));
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "changeLanguage",
          capture: "exception",
        })
      );
      console.error("Error al cambiar idioma:", error);
    }
  };

  const onLogout = () => {
    dispatch(startLogout());
  };

  const onOpenAdHocSupport = () => {
    setIsModalAdHocSupportOpen(true);
  };

  const onCloseModalAdHocSupport = () => {
    setIsModalAdHocSupportOpen(false);
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          top: navbarTop,
          boxShadow: "none",
          backgroundImage: "none",
          backgroundColor: "background.paper",
          color: "background.paperContrastText",
          borderBottom: `1px solid ${INPUT_COLOR_BORDER}`,
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            px: {
              md: PADDING_DESKTOP,
              xs: PADDING_MOBILE,
            },
            ...(isOwner ? ownerChrome.toolbar : {}),
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            {showMobileNavToggle && (
              <IconButton
                size="large"
                edge="start"
                onClick={() => dispatch(setOpenSlideMenu(!slideMenuIsOpen))}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Link
              to={homeRoute}
              style={
                isOwner
                  ? ownerChrome.logoLink
                  : {
                      display: "flex",
                      width: "65%",
                      justifyContent: "flex-start",
                      ...(hideAppBarLogo ? { visibility: "hidden" } : {}),
                    }
              }
            >
              <Avatar
                src={theme.logo}
                alt={`Logo ${NAME_BUSINESS}`}
                variant="square"
                sx={{
                  width: "60px",
                  ".MuiAvatar-img": {
                    maxWidth: "105px",
                    objectFit: "contain",
                  },
                }}
              />
            </Link>
          </Box>
          <Box
            className="buttons-nav"
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "center",
              gap: 3,
              flexWrap: "nowrap",
              justifyContent: "flex-start",
              flexShrink: 0,
              ...(isOwner ? ownerChrome.actions : {}),
            }}
          >
            {!isOwner && (
              <>
                {!isMobile && (
                  <>
                    {showBuyCreditsButton && (
                      <Box
                        onClick={() =>
                          dispatch(
                            setModalOpenState("isModalPurchaseCredits", true)
                          )
                        }
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                      >
                        <Typography sx={NAV_TEXT_ACTION_SX}>
                          {t(BUY_CREDITS)}
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
                <UsageCounter />
              </>
            )}

            {isOwner && (
              <OwnerNavActions
                userLanguage={userLanguage}
                onChangeLanguage={changeLanguage}
                onLogout={onLogout}
              />
            )}

            {!isAnonymous && !isOwner && (
              <IconButton
                aria-label={t(HELP)}
                sx={{ ...NAV_ICON_BUTTON_SX, mt: NAV_ICON_TOP_OFFSET }}
                onClick={openTidioChat}
              >
                <HelpCircle
                  size={NAV_ICON_SIZE}
                  strokeWidth={NAV_ICON_STROKE_WIDTH}
                />
              </IconButton>
            )}

            {showAdHocSupportButton && (
              <IconButton
                aria-label={t(SUPPORT)}
                sx={{ ...NAV_ICON_BUTTON_SX, mt: NAV_ICON_TOP_OFFSET }}
                onClick={onOpenAdHocSupport}
              >
                <Headset
                  size={NAV_ICON_SIZE}
                  strokeWidth={NAV_ICON_STROKE_WIDTH}
                />
              </IconButton>
            )}

            {!isOwner && !isMobile && (
              <Box sx={{ mt: NAV_ICON_TOP_OFFSET }}>
                <GlobalAlertsInbox />
              </Box>
            )}

            {!isOwner && (
              <LanguageSwitcher
                language={userLanguage || "en"}
                onChangeLanguage={changeLanguage}
                colorButton="secondary"
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
                sx={{
                  mt: NAV_ICON_TOP_OFFSET,
                  height: 42,
                  maxHeight: 42,
                  minWidth: "max-content",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "none !important",
                  boxShadow: "none !important",
                  backgroundColor: "transparent !important",
                  pl: "12px !important",
                  px: "0 !important",
                  "&.MuiButtonBase-root.MuiButton-root:hover": {
                    backgroundColor: "transparent !important",
                    border: "none !important",
                    boxShadow: "none !important",
                    color: "inherit !important",
                  },
                }}
                sxMenu={{
                  "& .MuiPaper-root": {
                    marginTop: "8px",
                  },
                }}
              />
            )}

            {isAnonymous ? (
              <ButtonCreateAccountFromAnonymous
                color="secondary"
                hasShadow={false}
                sx={{
                  ...NAV_TEXT_ACTION_SX,
                  height: "auto",
                  minHeight: "auto",
                  maxHeight: "none",
                  minWidth: "max-content",
                  p: 0,
                  px: "0 !important",
                  py: 0,
                  border: "none",
                  boxShadow: "none",
                  "&.MuiButtonBase-root.MuiButton-root:hover": {
                    backgroundColor: "transparent !important",
                    border: "none !important",
                    boxShadow: "none !important",
                    color: "inherit !important",
                  },
                }}
              />
            ) : null}
            {!isOwner && (
              <Box id="navbar-advertiser-selector">
                <SelectAdvertiserID sx={{ width: 185 }} />
              </Box>
            )}
          </Box>
          {!isOwner && isMobile && (
            <Box sx={{ mt: NAV_ICON_TOP_OFFSET }}>
              <GlobalAlertsInbox />
            </Box>
          )}
        </Toolbar>
      </AppBar>
      {!isOwner && !ROUTES_WITHOUT_SLIDE_MENU.includes(location.pathname) && (
        <SlideMenu
          onClickBuyCredits={() =>
            dispatch(setModalOpenState("isModalPurchaseCredits", true))
          }
          showBuyCreditsButton={showBuyCreditsButton}
        />
      )}
      {showAdHocSupportButton && (
        <ModalAdHocSupport
          modalOpen={isModalAdHocSupportOpen}
          onCloseModal={onCloseModalAdHocSupport}
        />
      )}
    </>
  );
};

export default NavBar;
